import { getUserExternalDirectoryId } from 'accelerator-aad/lib/getUserExternalDirectoryId';
import { logGreyError } from 'owa-analytics';
import { addMinutes, getTimestamp, now, startOfDay } from 'owa-datetime';
import { getUserTimeZone } from 'owa-session-store';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

import { db, placesPeopleTable, peopleTimezoneSettingsTable } from '../database';

import type { PeopleInfo } from '../database';
/**
 * @param peopleInfo: the list of peopleInfo to add to the cache
 */
export function addPeopleInfoToCache(peopleInfo: PeopleInfo[]): void {
    try {
        const ownerId = getUserExternalDirectoryId();
        const currentTimeZone = getUserTimeZone();
        const input = peopleInfo.map(info => {
            // We are using the email and start of date as the uniqueId to avoid duplicates that
            // changing collaboratorType or location might cause
            const uniqueId = `${info.email}-${getTimestamp(startOfDay(info.date))}`;

            return {
                uniqueId,
                email: info.email,
                location: info.location,
                collaboratorType: info.collaboratorType,
                startOfDay: getTimestamp(startOfDay(info.date)),
                expirationTime: getTimestamp(addMinutes(now(), 10)),
                owner: ownerId,
            };
        });

        // Refresh people rows, the timezone marker, and any stale-timezone cleanup as a
        // single readwrite transaction. If the stored timezone marker no longer matches
        // the current session timezone, this owner's existing rows were computed under
        // the old timezone (their startOfDay values are stale and carry different
        // uniqueIds, so bulkPut would not overwrite them). Deleting them in the SAME
        // transaction that updates the marker guarantees the new marker can never be
        // observed while stale rows still linger -- closing the race with the
        // out-of-band cache maintenance in clearPlacesPeopleCacheIfTimezoneChanged.
        void db
            .transaction('rw', placesPeopleTable, peopleTimezoneSettingsTable, async () => {
                const storedTimezone = await peopleTimezoneSettingsTable.get(ownerId);
                if (storedTimezone?.timezone !== currentTimeZone) {
                    await placesPeopleTable.where('owner').equals(ownerId).delete();
                }

                await peopleTimezoneSettingsTable.put({
                    uniqueId: ownerId,
                    timezone: currentTimeZone,
                });

                // bulkPut here will overwrite any existing entries with the same uniqueId
                // for every object in the input array
                await placesPeopleTable.bulkPut(input);
            })
            .catch(error => {
                logGreyError(
                    'PlacesPeopleCache_addPeopleInfoToCache',
                    getModuleContextMailboxInfo(),
                    error instanceof Error ? error : undefined
                );
            });
    } catch (error) {
        logGreyError(
            'PlacesPeopleCache_addPeopleInfoToCache',
            getModuleContextMailboxInfo(),
            error instanceof Error ? error : undefined
        );
    }
}
